import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  TableContainer,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CommonService } from "../services/CommonService";
import {
  IProductionPlan,
  IProductionPlanItem,
  IProductionPlanSelection,
} from "../common/interfaces";

interface ProductionDailyPlanProps {
  warehouseId: number;
  handleCancel: () => void;
  onContinue: (selection: IProductionPlanSelection[]) => void;
  onSkipToExecute: () => void;
  /**
   * Selección con la que se salió al paso de ejecución. Se restaura al volver
   * para no perder los ajustes hechos a mano; solo aplica al montar, un
   * "Actualizar" posterior sí recalcula desde cero.
   */
  restoreSelection?: IProductionPlanSelection[];
}

/** Motivos por los que Prophet no aportó pronóstico, en lenguaje del negocio. */
const PROPHET_REASONS: Record<string, string> = {
  feature_disabled: "el pronóstico está desactivado",
  unreachable: "el servicio no responde",
  // Los modelos son por par (producto, almacén): que exista en otra sucursal
  // no sirve aquí, y el mensaje tiene que dejarlo claro.
  no_models: "ningún producto tiene modelo entrenado para este almacén",
  no_model: "sin modelo entrenado para este almacén",
  service_error: "el servicio devolvió un error",
  timeout_budget: "se agotó el tiempo de espera",
  exception: "ocurrió un error al consultarlo",
};

const cellStyle: React.CSSProperties = {
  borderBottom: "1px solid #555",
  padding: "6px 8px",
  verticalAlign: "middle",
};

const headStyle: React.CSSProperties = {
  ...cellStyle,
  borderBottom: "2px solid #777",
  position: "sticky",
  top: 0,
  background: "#333",
  zIndex: 1,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const num = (value: number) => (Number.isInteger(value) ? `${value}` : value.toFixed(2));

/**
 * Formatea según la unidad del producto. Los que van por pieza no admiten
 * fracciones: mostrar "26.29 ratones" es ruido, no precisión. Se redondea al
 * entero, salvo cuando eso convertiría una demanda real en un 0 engañoso.
 */
const formatQty = (value: number, isInteger: boolean) => {
  if (!isInteger) return num(value);
  if (value === 0) return "0";

  const rounded = Math.round(value);
  if (rounded !== 0) return `${rounded}`;

  return value > 0 ? "<1" : ">-1";
};

/** "miércoles" -> "miércoles", "sábado" -> "sábados". */
const pluralWeekday = (label: string) => (label.endsWith("s") ? label : `${label}s`);

/**
 * Para buscar sin pelearse con los acentos ni las mayúsculas: "jalapeno"
 * tiene que encontrar "JALAPEÑO".
 */
const normalize = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** "pieza" pluraliza, "kg" no. */
const unitLabel = (quantity: number, unit: string) =>
  unit === "pieza" && quantity !== 1 ? "piezas" : unit;

export const ProductionDailyPlan = (props: ProductionDailyPlanProps) => {
  const { warehouseId, handleCancel, onContinue, onSkipToExecute, restoreSelection } = props;

  const [plan, setPlan] = useState<IProductionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [includeScheduled, setIncludeScheduled] = useState(true);
  const [onlyMissing, setOnlyMissing] = useState(false);
  // Existencia y salidas del día son datos de diagnóstico: útiles cuando algo
  // no cuadra, ruido el resto del tiempo. Ocultos salvo que se pidan.
  const [showDetail, setShowDetail] = useState(false);
  const [search, setSearch] = useState("");

  // Estado editable por fila.
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [quantities, setQuantities] = useState<Record<number, string>>({});
  const [recipeChoice, setRecipeChoice] = useState<Record<number, number>>({});

  // La restauración se consume una sola vez: al volver del paso de ejecución.
  const pendingRestore = useRef(restoreSelection);

  const loadPlan = useCallback(() => {
    if (!warehouseId) {
      setError("No hay un almacén asignado al usuario.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    CommonService.getProductionDailyPlan(warehouseId, { date, includeScheduled })
      .then((res) => {
        if (!res || !res.success || !res.object) {
          setError(res?.message || "No se pudo generar el plan de producción.");
          setPlan(null);
          return;
        }

        const data = res.object;
        setPlan(data);
        setLastUpdated(new Date());

        // Las cantidades vienen precargadas con lo sugerido, pero nada arranca
        // marcado: producir es una decisión del operador, no algo que el plan
        // deba dar por hecho.
        const nextChecked: Record<number, boolean> = {};
        const nextQuantities: Record<number, string> = {};
        const nextRecipes: Record<number, number> = {};

        data.items.forEach((item) => {
          nextChecked[item.product_id] = false;
          nextQuantities[item.product_id] =
            item.suggested_quantity > 0 ? String(item.suggested_quantity) : "";
          nextRecipes[item.product_id] = item.default_recipe_id;
        });

        const restore = pendingRestore.current;
        if (restore && restore.length > 0) {
          // Nada queda marcado salvo lo que el operador traía seleccionado.
          Object.keys(nextChecked).forEach((key) => {
            nextChecked[Number(key)] = false;
          });
          restore.forEach((sel) => {
            nextChecked[sel.productId] = true;
            nextQuantities[sel.productId] = String(sel.quantity);
            nextRecipes[sel.productId] = sel.recipeId;
          });
          pendingRestore.current = undefined;
        }

        setChecked(nextChecked);
        setQuantities(nextQuantities);
        setRecipeChoice(nextRecipes);
      })
      .catch(() => {
        setError("No se pudo conectar con el servidor.");
        setPlan(null);
      })
      .finally(() => setLoading(false));
  }, [warehouseId, date, includeScheduled]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const buildSelection = (): IProductionPlanSelection[] => {
    if (!plan) return [];
    return plan.items
      .filter((item) => checked[item.product_id] && Number(quantities[item.product_id]) > 0)
      .map((item) => ({
        recipeId: recipeChoice[item.product_id] ?? item.default_recipe_id,
        productId: item.product_id,
        quantity: Number(quantities[item.product_id]),
      }));
  };

  const selection = buildSelection();
  const totalUnits = selection.reduce((acc, s) => acc + s.quantity, 0);

  const query = normalize(search.trim());

  const visibleItems: IProductionPlanItem[] = plan
    ? plan.items
        .filter((item) => (onlyMissing ? item.suggested_quantity > 0 : true))
        // Se busca también por nombre de receta: es lo que distingue a los
        // productos que tienen más de una.
        .filter(
          (item) =>
            !query ||
            normalize(item.product_name).includes(query) ||
            item.recipes.some((r) => normalize(r.name).includes(query))
        )
    : [];

  // El maestro opera sobre lo que se está viendo, no sobre el plan completo:
  // marcar filas que el filtro esconde sería una sorpresa desagradable.
  const selectableItems = visibleItems;
  const selectedVisible = selectableItems.filter((item) => checked[item.product_id]).length;
  const allVisibleSelected =
    selectableItems.length > 0 && selectedVisible === selectableItems.length;
  const someVisibleSelected = selectedVisible > 0 && !allVisibleSelected;

  const toggleAllVisible = (next: boolean) => {
    const updated = { ...checked };
    selectableItems.forEach((item) => {
      updated[item.product_id] = next;
    });
    setChecked(updated);
  };

  const prophetDegraded = plan ? plan.meta.prophet.reason !== "ok" : false;

  /**
   * El desglose del sugerido en lenguaje llano. La cuenta es siempre
   * demanda estimada + agendados - existencia, pero el operador no tiene por
   * qué deducirla de las columnas.
   */
  const explainSuggestion = (item: IProductionPlanItem): string[] => {
    const usedProphet = item.suggested_source === "prophet";
    const lines: string[] = [];
    const safetyPct = Math.round(((plan?.meta.safety_factor ?? 1) - 1) * 100);

    if (usedProphet) {
      lines.push(
        `Se estiman ${formatQty(item.prophet.quantity ?? 0, item.is_integer)} ${unitLabel(item.prophet.quantity ?? 0, item.unit)} para hoy, según el pronóstico del modelo.`
      );
      if (item.prophet.remaining_today !== null) {
        lines.push(`De eso, ${formatQty(item.prophet.remaining_today, item.is_integer)} corresponde a lo que resta del día.`);
      }
    } else {
      lines.push(
        `Se estiman ${formatQty(item.historical.quantity, item.is_integer)} ${unitLabel(item.historical.quantity, item.unit)} para hoy: es lo que salió en promedio los últimos ${
          item.historical.days_considered
        } ${pluralWeekday(plan?.weekday_label ?? "días")}, más ${safetyPct}% de seguridad.`
      );
      if (item.historical.days_with_sales < item.historical.days_considered) {
        lines.push(
          `Ojo: solo hubo movimiento en ${item.historical.days_with_sales} de esos ${item.historical.days_considered} días, así que el promedio es menos confiable.`
        );
      }
    }

    if (includeScheduled && item.scheduled.quantity > 0) {
      lines.push(`+ ${formatQty(item.scheduled.quantity, item.is_integer)} de pedidos ya agendados para hoy.`);
    }

    if (item.stock_negative) {
      lines.push(
        `La existencia marca ${formatQty(item.stock, item.is_integer)} (negativo por producciones que no se registraron), así que no se descuenta nada.`
      );
    } else if (item.stock > 0) {
      lines.push(`− ${formatQty(item.stock, item.is_integer)} ${unitLabel(item.stock, item.unit)} que ya hay en existencia.`);
    }

    lines.push(`= ${formatQty(item.suggested_quantity, item.is_integer)} ${unitLabel(item.suggested_quantity, item.unit)} a producir.`);

    if (item.is_integer) {
      lines.push("Se redondea hacia arriba: quedarse corto sale más caro que sobrar.");
    }

    return lines;
  };

  /**
   * De cuál de las dos columnas salió el número. Se queda en una palabra a
   * propósito: el encabezado ya dice la ventana, y en modo detalle la columna
   * se estrecha y cualquier frase larga se parte en dos renglones.
   */
  const sourceLabel = (item: IProductionPlanItem) => {
    const base = item.suggested_source === "prophet" ? "Pronóstico" : "Promedio";
    return includeScheduled && item.scheduled.quantity > 0 ? `${base} + agend.` : base;
  };

  const renderIngredients = (item: IProductionPlanItem) => {
    const { status, max_producible, limiting } = item.ingredients;

    if (status === "unknown") {
      return (
        <Tooltip title="La receta no tiene insumos configurados">
          <Chip label="?" size="small" variant="outlined" />
        </Tooltip>
      );
    }

    const label =
      status === "ok"
        ? "OK"
        : status === "partial"
        ? `Parcial (${formatQty(max_producible ?? 0, item.is_integer)})`
        : "Insuficiente";
    const color: "success" | "warning" | "error" =
      status === "ok" ? "success" : status === "partial" ? "warning" : "error";

    const detail =
      limiting.length > 0
        ? limiting
            .map(
              (l) =>
                `${l.name}: requiere ${formatQty(l.required, l.is_integer)}, hay ${formatQty(
                  l.available,
                  l.is_integer
                )}`
            )
            .join(" · ")
        : "Alcanzan los insumos en existencia";

    return (
      <Tooltip title={detail}>
        <Chip label={label} size="small" color={color} variant="outlined" />
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Cargando plan del día…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Plan de Producción
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadPlan}>
          Reintentar
        </Button>
        {/* Que una falla del plan no bloquee producir */}
        <Button variant="outlined" sx={{ ml: 2 }} onClick={onSkipToExecute}>
          Ir directo a Ejecutar
        </Button>
        <Button variant="text" sx={{ ml: 2 }} onClick={handleCancel}>
          Cancelar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Plan de Producción — {plan?.weekday_label} {plan ? moment(plan.date).format("DD/MM/YYYY") : ""}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {plan?.warehouse.name}
          {lastUpdated ? ` · Última actualización: ${moment(lastUpdated).format("HH:mm:ss")}` : ""}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Buscar producto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
              endAdornment: search ? (
                <IconButton
                  size="small"
                  aria-label="Limpiar búsqueda"
                  onClick={() => setSearch("")}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : undefined,
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            type="date"
            size="small"
            label="Fecha"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <ToggleButtonGroup
            value={onlyMissing ? "missing" : "all"}
            exclusive
            size="small"
            onChange={(_, v) => v !== null && setOnlyMissing(v === "missing")}
          >
            <ToggleButton value="all">TODOS</ToggleButton>
            <ToggleButton value="missing">SOLO CON FALTANTE</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeScheduled}
                onChange={(e) => setIncludeScheduled(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Incluir agendados</Typography>}
          />
          <Tooltip title="Muestra la existencia actual y lo que ha salido en el día">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showDetail}
                  onChange={(e) => setShowDetail(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Ver detalle</Typography>}
            />
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadPlan}>
            Actualizar
          </Button>
        </Box>
      </Box>

      {prophetDegraded && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Pronóstico Prophet no disponible ({PROPHET_REASONS[plan?.meta.prophet.reason ?? ""] ??
            plan?.meta.prophet.reason}
          ). Se usa el promedio histórico.
        </Alert>
      )}

      {plan && plan.items.length === 0 && (
        <Alert severity="info">No hay recetas de producción activas.</Alert>
      )}

      {/* Sin resultados por la búsqueda: no es que no haya nada que producir. */}
      {plan && plan.items.length > 0 && visibleItems.length === 0 && query !== "" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ningún producto coincide con "{search.trim()}".
        </Alert>
      )}

      {plan && plan.items.length > 0 && visibleItems.length === 0 && query === "" && onlyMissing && (
        <Alert severity="success" sx={{ mb: 2 }}>
          El inventario cubre la demanda estimada de hoy. Cambia a "TODOS" para ver el detalle.
        </Alert>
      )}

      {visibleItems.length > 0 && (
        <TableContainer component={Paper}>
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...headStyle, width: 40 }}>
                    <Tooltip
                      title={allVisibleSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                    >
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        onChange={(e) => toggleAllVisible(e.target.checked)}
                      />
                    </Tooltip>
                  </th>
                  <th style={headStyle}>Producto</th>
                  {showDetail && (
                    <>
                      <th style={{ ...headStyle, textAlign: "right" }}>Exist.</th>
                      <th style={{ ...headStyle, textAlign: "right" }}>Salidas hoy</th>
                    </>
                  )}
                  <th style={{ ...headStyle, textAlign: "right" }}>
                    Prom. {plan?.weekday_label} ({plan?.meta.weeks} sem)
                  </th>
                  <th style={{ ...headStyle, textAlign: "right" }}>Pronóstico</th>
                  <th style={{ ...headStyle, textAlign: "right" }}>Agendado</th>
                  <th style={{ ...headStyle, textAlign: "right" }}>Sugerido</th>
                  <th style={{ ...headStyle, width: 140 }}>A producir</th>
                  <th style={headStyle}>Insumos</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <tr
                    key={item.product_id}
                    // Sombreado tenue en azul: se lee como "seleccionado" y no
                    // se confunde con el hover.
                    style={
                      checked[item.product_id]
                        ? { backgroundColor: "rgba(144, 202, 249, 0.12)" }
                        : undefined
                    }
                  >
                    <td style={cellStyle}>
                      <Checkbox
                        size="small"
                        checked={!!checked[item.product_id]}
                        onChange={(e) =>
                          setChecked({ ...checked, [item.product_id]: e.target.checked })
                        }
                      />
                    </td>
                    <td style={cellStyle}>
                      <Typography variant="body2">{item.product_name}</Typography>
                      {item.recipes.length > 1 ? (
                        <Select
                          size="small"
                          variant="standard"
                          value={recipeChoice[item.product_id] ?? item.default_recipe_id}
                          onChange={(e) =>
                            setRecipeChoice({
                              ...recipeChoice,
                              [item.product_id]: Number(e.target.value),
                            })
                          }
                          sx={{ fontSize: "0.75rem", mt: 0.5 }}
                        >
                          {item.recipes.map((r) => (
                            <MenuItem key={r.id} value={r.id} sx={{ fontSize: "0.75rem" }}>
                              {r.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {item.recipes[0]?.name}
                        </Typography>
                      )}
                    </td>
                    {showDetail && (
                      <>
                        <td style={{ ...cellStyle, textAlign: "right" }}>
                          {item.stock_negative ? (
                            <Tooltip title="Existencia en negativo: hay salidas registradas sin la producción correspondiente. No se descuenta del sugerido.">
                              <Typography variant="body2" sx={{ color: "warning.main" }}>
                                {formatQty(item.stock, item.is_integer)} ⚠
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2">{formatQty(item.stock, item.is_integer)}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {item.unit}
                          </Typography>
                        </td>
                        <td style={{ ...cellStyle, textAlign: "right" }}>
                          <Tooltip title="Lo que ha salido hoy de este producto: venta directa más lo consumido como insumo de otros productos.">
                            <Typography variant="body2" color="text.secondary">
                              {formatQty(item.sold_today, item.is_integer)}
                            </Typography>
                          </Tooltip>
                        </td>
                      </>
                    )}
                    <td style={{ ...cellStyle, textAlign: "right" }}>
                      <Tooltip
                        title={`Promedio diario crudo: ${formatQty(item.historical.raw_daily_avg, item.is_integer)} · ${
                          item.historical.days_with_sales
                        } de ${item.historical.days_considered} días con venta · +${Math.round(
                          ((plan?.meta.safety_factor ?? 1) - 1) * 100
                        )}% de seguridad`}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              item.historical.confidence === "high" ? undefined : "warning.main",
                          }}
                        >
                          {formatQty(item.historical.quantity, item.is_integer)}
                        </Typography>
                      </Tooltip>
                    </td>
                    <td style={{ ...cellStyle, textAlign: "right" }}>
                      {item.prophet.quantity !== null ? (
                        <Tooltip
                          title={
                            item.prophet.remaining_today !== null
                              ? `Resta del día: ${formatQty(item.prophet.remaining_today, item.is_integer)}`
                              : ""
                          }
                        >
                          <Typography variant="body2">{formatQty(item.prophet.quantity, item.is_integer)}</Typography>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            PROPHET_REASONS[item.prophet.reason ?? ""] ??
                            item.prophet.reason ??
                            "No disponible"
                          }
                        >
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        </Tooltip>
                      )}
                    </td>
                    <td style={{ ...cellStyle, textAlign: "right" }}>
                      {item.scheduled.quantity > 0 ? (
                        <Tooltip
                          title={item.scheduled.orders
                            .map(
                              (o) =>
                                `Venta ${o.venta_id}${
                                  o.schedule ? ` (${moment(o.schedule).format("HH:mm")})` : ""
                                }: ${formatQty(o.quantity, item.is_integer)}`
                            )
                            .join(" · ")}
                        >
                          <Typography variant="body2" sx={{ color: "info.main" }}>
                            {formatQty(item.scheduled.quantity, item.is_integer)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </td>
                    <td style={{ ...cellStyle, textAlign: "right" }}>
                      <Tooltip
                        title={
                          <Box>
                            {explainSuggestion(item).map((line, i) => (
                              <Typography
                                key={i}
                                variant="caption"
                                component="div"
                                sx={{ mb: 0.5 }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        }
                      >
                        <Box sx={{ cursor: "help" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatQty(item.suggested_quantity, item.is_integer)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {sourceLabel(item)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </td>
                    <td style={cellStyle}>
                      <TextField
                        type="number"
                        size="small"
                        value={quantities[item.product_id] ?? ""}
                        onChange={(e) =>
                          setQuantities({ ...quantities, [item.product_id]: e.target.value })
                        }
                        inputProps={{ step: item.is_integer ? 1 : 0.01, min: 0 }}
                      />
                    </td>
                    <td style={cellStyle}>{renderIngredients(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </TableContainer>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selection.length} producto{selection.length === 1 ? "" : "s"} seleccionado
          {selection.length === 1 ? "" : "s"} · {num(Number(totalUnits.toFixed(2)))} unidades
        </Typography>
        <Box>
          <Button variant="contained" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button variant="text" sx={{ ml: 2 }} onClick={onSkipToExecute}>
            Ejecutar sin plan
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 2 }}
            disabled={selection.length === 0}
            onClick={() => onContinue(selection)}
          >
            Ir a Ejecutar ({selection.length})
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
